import { EmptyObject } from './../../types/handlers';
import { CalcUtil, SupplCalcInput, SupplCalcInfo, SupplDefaultData } from './../../types/project-config';
import { IconSymbols } from 'app/types/drawing/symbol-set';
import { SymbolIDs, SymbolInfo, SymbolSizes, SymbolSet } from './../../types/drawing/symbol-set';
import { LineStyleSwatchComponent } from './attribute-option-line-styler/line-style-swatch/line-style-swatch.component';
import { AttributeOptionLineStylerComponent } from './attribute-option-line-styler/attribute-option-line-styler.component';
import { DashID, LineThicknesses, LineThicknessKeys } from 'app/types/drawing/dashed-lines';
import { Component, Input, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, ElementRef, HostBinding } from '@angular/core';
import { AttributeFeatureType, ConfigAttribute, AttributeOption, Costs, PricePoint, CostKeys } from 'app/types/project-config';
import { Project } from 'app/model/project';
import { SimpleHandler, SimpleTypedHandler } from 'app/types/handlers';
import { AttributeOptionPointStylerComponent } from './attribute-option-point-styler/attribute-option-point-styler.component';
import { AttributeOptionPointSwatchComponent } from './attribute-option-point-styler/attribute-option-point-swatch/attribute-option-point-swatch.component';
import { IDKeyTypes } from 'app/model/id-manager';


@Component({
  selector: 'app-attribute-option-editor',
  templateUrl: './attribute-option-editor.component.html',
  styleUrls: ['./attribute-option-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AttributeOptionEditorComponent implements OnInit {
  private _ready = false;
  private _color: string = '#000000';
  public showDashes = false;
  public showSymbols = true; // change to default false

  set color(c: string) {
    this._color = c;
    this.Refresh();
  }
  get color(): string {
    return this._color;
  }
  private _showPrices: boolean = false;
  public set showPrices(show: boolean) {
    this._showPrices = show;
    this.Refresh();
  }
  public get showPrices(): boolean {
    return this._showPrices;
  }

  private _showStyle: boolean = false;
  public set showStyle(show: boolean) {
    this._showStyle = show;
    if (!this._showStyle) {
      this.showDashes = false;
      this.showSymbols = false;
    }
    this.Refresh();
  }
  public get showStyle(): boolean {
    return this._showStyle;
  }


  private _thickness: LineThicknessKeys = 'medium';
  @Input()
  set thickness(t: LineThicknessKeys) {
    this._thickness = t;
  }
  get thickness(): LineThicknessKeys {
    return this._thickness;
  }

  private _symbolSize: SymbolSizes = 'normal';
  set symbolSize(size: SymbolSizes) {
    this._symbolSize = size;
    this.Refresh();
  }
  get symbolSize(): SymbolSizes {
    return this._symbolSize;
  }

  get lineThickness(): LineThicknesses {
    return LineThicknesses[this._thickness];
  }

  private _selectedDash: DashID = DashID.solid;
  set selectedDash(sel: DashID) {
    this._selectedDash = sel;
    this.Refresh();
  }
  get selectedDash(): DashID {
    return this._selectedDash;
  }

  private _project?: Project;
  @Input()
  set project(prj: Project | undefined) {
    this._project = prj;
    if (this._project) {
      this.Refresh();
    }
  }
  get project(): Project | undefined {
    return this._project;
  }

  private _attribute?: ConfigAttribute;
  @Input()
  public set attribute(att: ConfigAttribute | undefined) {
    this._attribute = att;
    if (!(this._project && this._attribute)) {
      this.Defer(() => {
        this.attribute = att;
      });
      return;
    }
    const type = this._project.GetFeatureType(this._attribute.id);
    this._attributeType = (type === false) ? undefined : type;
    this.Refresh();
  }
  public get attribute(): ConfigAttribute | undefined {
    return this._attribute;
  }

  private _optionName = '';
  public set optionName(name: string) {
    this._optionName = name;
    this.Refresh();
  }
  public get optionName(): string {
    return this._optionName;
  }

  private _option?: AttributeOption;
  @Input() set option(opt: AttributeOption | undefined) {
    this._option = opt;
    this.showDashes = false;
    this.showSymbols = false;
    if (this._option && this._option.style && this._option.style.color) {
      const color: string = this._option.style.color;
      if (color.length > 7) {
        this._visible = false;
        this._color = color.substring(0, 7);
      } else {
        this._visible = true;
        this._color = color;
      }
    } else {
      this._visible = true;
      this._color = '#000000';
    }
    if (this._option && this._option.style && this._option.style.symbol) {
      this._attributeSymbol = this._option.style.symbol;
    } else {
      this._attributeSymbol = IconSymbols[SymbolIDs.SQUARE_SOLID];
    }
    if (this._option && this._option.cost) {
      let maxCost = Number.MIN_SAFE_INTEGER;
      for (const pricePt of Object.keys(this._costs)) {
        const key: CostKeys = pricePt as CostKeys;
        if (this._option.cost[key]) {
          this._costs[key] = this._option.cost[key];
        } else {
          this._costs[key] = 0;
        }
        if (this._costs[key] !== undefined) {
          maxCost = Math.max(this._costs[key] as number, maxCost);
        }
      }
      this._showPrices = (maxCost > 0);
    }
    this._optionName = (!!this._option) ? this._option.label : '';
    this._showStyle = (!!this._option && !!this._option.style);

    this.Refresh();
  }
  get option(): AttributeOption | undefined { return this._option; }

  private _attributeSymbol?: SymbolInfo = IconSymbols[SymbolIDs.SQUARE_SOLID];
  public set attributeSymbol(sym: SymbolInfo | undefined) {
    this._attributeSymbol = sym;
    this.Refresh();
  }
  public get attributeSymbol(): SymbolInfo | undefined {
    return this._attributeSymbol;
  }

  private _attributeType?: AttributeFeatureType;
  public get isSegment(): boolean {
    if (!this._attributeType) return false;
    return this._attributeType === 'segment';
  }
  public get isPoint(): boolean {
    if (!this._attributeType) return false;
    return this._attributeType === 'point';
  }
  public get isSuppl(): boolean {
    if (!this._attributeType) return false;

    return this._attributeType === 'suppl';
  }

  private _costs: Costs = {
    materials: 0.0,
    labor: 0.0
  }

  public get prices(): Array<PricePoint> {
    if (!(this.project && this.project.config)) return [];
    return this.project.config.prices;
  }
  private _visible = false;
  public set isVisible(vis: boolean) {
    this._visible = vis;
    this.showDashes = false;
    this.showSymbols = false;
    this.cdr.markForCheck();
    this.Refresh();
  }
  public get isVisible(): boolean {
    return this._visible;
  }
  public get visdisabled(): 'disabled' | null {
    return this.isVisible ? null : 'disabled';
  }
  public get canBeNamed(): Boolean {
    if (!this.attribute) return false;
    return (this.attribute.input !== 'selector');
  }



  @ViewChild('lineSwatches', { static: true }) lineSwatches?: AttributeOptionLineStylerComponent;
  @ViewChild('lineSwatch', { static: true }) lineSwatch?: LineStyleSwatchComponent;
  @ViewChild('symbolSwatches', { static: true }) symbolSwatches?: AttributeOptionPointStylerComponent;
  @ViewChild('symbolSwatch', { static: true }) symbolSwatch?: AttributeOptionPointSwatchComponent;

  constructor(private cdr: ChangeDetectorRef, private domNode: ElementRef) { }

  ngOnInit(): void {
    this._ready = true;
  }
  Refresh() {
    this.cdr.detectChanges();

  }
  ToggleDashes() {
    this.showDashes = !this.showDashes;
    if (this.showDashes === true) {
      if (this.lineSwatches) {
        this.lineSwatches.SelectSwatch(this.selectedDash);
      }
    }
    this.Refresh();
  }
  ToggleSymbols() {
    this.showSymbols = !this.showSymbols;
    if (this.showSymbols === true) {
      if (this.symbolSwatches) {
        // this.symbolSwatches.SelectSwatch(this.selectedDash);
      }
    }
  }
  HandleSwatchKey(event: KeyboardEvent) {
    switch (event.code) {
      case 'Enter':
      case 'Space':
        this.ToggleDashes();
        break;
    }
  }
  SymbolEntered() {
    this.showSymbols = false;
    this.Refresh();
  }
  DashEntered(dash: DashID) {
    this.selectedDash = dash;
    this.ToggleDashes();
    if (this.lineSwatch) {
      this.lineSwatch.Focus();
    }
  }
  IsEntryPrice(pricePoint: PricePoint): boolean {
    if (!this._attributeType) return false;
    return (pricePoint.entry && pricePoint.availability.indexOf(this._attributeType) > -1);
    return (pricePoint.name.substring(0, 1) === '_');
  }
  GetCostValue(pricePoint: PricePoint): number {
    const cost = this._costs[pricePoint.name as CostKeys];
    if(!cost) return NaN;
    return this._costs[pricePoint.name as CostKeys] as number;
  }
  SetCostValue(pricePoint: PricePoint, event: KeyboardEvent) {
    if (event && event.target && (event.target as HTMLInputElement).value) {
      this._costs[pricePoint.name as CostKeys] = Number((event.target as HTMLInputElement).value);
    }
  }
  GetOption(): AttributeOption | undefined {
    if (!this.project) return undefined;
    let returnOption = this._option;
    if (!returnOption) {
      returnOption = {
        id: this.project?.idManager.ID(IDKeyTypes.AttributeOption),
        label: this.optionName
      }
      this.project.idRegistry.RegisterID(returnOption.id, returnOption);
    }
    returnOption.label = this.optionName;

    if (this._costs) {
      returnOption.cost = JSON.parse(JSON.stringify(this._costs));
    }
    if (this.showStyle) {
      returnOption.style = {
        color: this.color
      }
      if (this.isPoint) {
        if (this.attributeSymbol) {
          returnOption.style.symbol = this.attributeSymbol;
        }
        if (this.symbolSize) {
          returnOption.style.size = this.symbolSize;
        }
      }
      if (this.isSegment) {
        if (this.selectedDash) {
          returnOption.style.pattern = this.selectedDash;
        }
        if (!!this.thickness) {
          returnOption.style.thickness = this.thickness;
        }
      }
    }
    return returnOption;
  }
  Defer(handler: SimpleHandler) {
    window.requestAnimationFrame(() => {
      handler();
    })
  }
}
